import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Modal } from 'react-bootstrap';
import { FaUtensils, FaClock, FaCheck } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { getOrdersApi, updateOrderStatusApi } from '../menu/service/api_service';
import { API_BASE_URL } from '../../config/api';

export default function KitchenBoard() {
  const token = localStorage.getItem('token');
  const [orders, setOrders] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [orderToConfirm, setOrderToConfirm] = useState(null);
  
  const fetchOrders = async () => {
    try {
      const res = await getOrdersApi(token, 'cooking');
      const data = res.data;
      if (Array.isArray(data)) {
        setOrders(data);
      } else {
        console.error('Invalid data format:', data);
        setOrders([]);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    }
  };

  useEffect(() => {
    fetchOrders();
    
    // Setup SSE for real-time updates
    const eventSource = new EventSource(`${API_BASE_URL}/api/orders/stream?token=${token}`);
    
    eventSource.addEventListener('ORDER_CREATED', (e) => {
      const newOrder = JSON.parse(e.data);
      if (newOrder.status === 'cooking') {
        setOrders(prev => [newOrder, ...prev]);
        // Simple beep via Web Audio API instead of loading a file
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioCtx.createOscillator();
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
        oscillator.connect(audioCtx.destination);
        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.1);
      }
    });

    eventSource.addEventListener('ORDER_UPDATED', (e) => {
      const updatedOrder = JSON.parse(e.data);
      if (updatedOrder.status !== 'cooking') {
        setOrders(prev => prev.filter(o => o.id !== updatedOrder.id));
      }
    });

    return () => {
      eventSource.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleReadyClick = (order) => {
    setOrderToConfirm(order);
    setShowConfirmModal(true);
  };

  const confirmReady = async () => {
    if (!orderToConfirm) return;
    const id = orderToConfirm.id;
    try {
      await updateOrderStatusApi(token, id, { status: 'ready' });
      setOrders(prev => prev.filter(o => o.id !== id));
      setShowConfirmModal(false);
      setOrderToConfirm(null);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Network error", "error");
    }
  };

  return (
    <Container fluid className="px-2 py-3" style={{ backgroundColor: "#f4f7fa", minHeight: "100%" }}>
      <h4 className="fw-bold text-dark mb-4 px-2"><FaUtensils className="me-2 text-warning" /> Kitchen Board</h4>
      <Row className="g-3 px-2">
        {orders.length === 0 ? (
           <Col xs={12} className="text-center py-5 text-muted">
             <FaUtensils size={48} className="mb-3 opacity-25" />
             <h5>No orders in queue</h5>
           </Col>
        ) : orders.map(order => (
          <Col xs={12} sm={6} md={6} lg={4} xl={3} key={order.id}>
            <Card className="border-0 shadow-sm h-100 rounded-4" style={{ overflow: 'hidden' }}>
              <div style={{ height: '4px', backgroundColor: '#ffc107', width: '100%' }}></div>
              <Card.Header className="bg-white border-bottom-0 pt-3 pb-0 d-flex justify-content-between align-items-center">
                <Badge bg="warning" text="dark" className="fs-6 py-2 px-3 rounded-pill">#{order.id}</Badge>
                <div className="text-end">
                  <div className="text-muted small fw-medium">
                    Order: {new Date(order.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>
                  <div className="text-danger fw-bold small">
                    <FaClock className="me-1"/> 
                    Due: {order.delivery_time ? new Date(order.delivery_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'ASAP'}
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="d-flex flex-column">
                <div className="mb-3 pb-2 border-bottom fw-bold" style={{ color: '#0d6efd', fontSize: '1.1rem' }}>
                  {order.recipient_name || "Walk-in"}
                </div>
                <div className="d-flex flex-column gap-2 mb-4 flex-grow-1">
                  {order.items && order.items.map((item, idx) => (
                    <div key={idx} className="p-2 bg-light rounded-3 border">
                      <div className="d-flex align-items-start fw-bold text-dark">
                        <span className="text-primary me-2">{item.quantity}x</span>
                        <span style={{ lineHeight: '1.2' }}>{item.product_name}</span>
                      </div>
                      {item.modifiers && item.modifiers.length > 0 && (
                        <div className="small text-muted ms-4 mt-1 fw-medium">
                          {item.modifiers.map(m => m.modifier_name).join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <Button 
                  variant="success" 
                  className="w-100 py-3 fw-bold d-flex justify-content-center align-items-center shadow-sm rounded-3 mt-auto"
                  style={{ fontSize: '1.2rem', transition: 'all 0.2s' }}
                  onClick={() => handleReadyClick(order)}
                >
                  <FaCheck className="me-2" /> Ready to Serve
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-success">Confirm Order Ready</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Are you sure order <strong>#{orderToConfirm?.id}</strong> is ready to be served?
          </p>
          <div className="bg-light p-3 rounded mb-2">
            <div className="fw-bold mb-2">Items:</div>
            {orderToConfirm?.items?.map((item, idx) => (
              <div key={idx} className="small d-flex justify-content-between border-bottom pb-1 mb-1">
                <span>{item.quantity}x {item.product_name}</span>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="light" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
          <Button variant="success" className="px-4 fw-bold shadow-sm" onClick={confirmReady}>Confirm Ready</Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}
